import React, { FunctionComponent, useEffect, useState } from "react";
import {
    colorTwoBright,
    colorTwoDark,
} from "../../../../globalStyles/colors/scoutColors";
import { IChartMergedDatapoint, IGPSData } from "../../../../Models/IGPSData";
import {
    format24Hr,
    formatDOW,
    formatWeek,
    ReportChartGroup,
    ReportScrollableSection,
    ReportSectionHeader,
    ReportSectionNumber,
    ReportH1,
    NivoCalendar,
    TwoColumnGrid,
    AnimatedReportCard,
} from "../../../Common";
import {
    mergeGPSData,
    mergeWeeklyData,
} from "../../RetailMatch/Logic/SharedMethods";
import {
    IChartCalendarDatapoint,
    IChartVisitVisitorDatapoint,
} from "../../../../Models/Models";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/RenderType";
import { GPSRelVolMet } from "./GPSRelVolMet";
import { CHILD_HEIGHT } from "../../../Common/DigitalShare/DigitalShareGenerator";
import { HH_ATTR_STATIC_TEXT } from "../../../../Util/ScoutDigitalShareUtil";
import MobilyticsPolygonSelect from "../MobilyticsPolygonSelect";
import { IMobilyticsBenchmarkItem } from "../../../../Models/IMobilyticsBenchmarkItem";
import { IIdentifier } from "../../../../Models/IIdentifier";
import { GPSVolumeMetrics } from "./GPSVolumeMetrics";

interface IGPSInsightsVolumeProps {
    storeData: IGPSData;
    location1Name: string;
    location2Name?: string;
    benchmarkData?: IGPSData;
    visitsCount?: IGPSData;
    renderType: RenderType;
    benchmarkItems: IMobilyticsBenchmarkItem[];
}

enum SelectionTypesEnum {
    SELECTION_ONE_PRIMARY = "visitorWeight",
    SELECTION_ONE_SECONDARY = "visitWeight",
}

const GPSInsightsVolume: FunctionComponent<IGPSInsightsVolumeProps> = (
    props: IGPSInsightsVolumeProps
) => {
    const { storeData, benchmarkData, location1Name, location2Name } = props;

    const [mergedCalendarData, setMergedCalendarData] = useState<
        IChartMergedDatapoint[]
    >([]);
    const [dayCalendarData, setDayCalendarData] = useState<
        Map<string, IChartVisitVisitorDatapoint[]>
    >(new Map());
    const [calendarSelectionOneData, setCalendarSelectionOneData] = useState<
        IChartCalendarDatapoint[]
    >([]);
    const [calendarSelectionTwoData, setCalendarSelectionTwoData] = useState<
        IChartCalendarDatapoint[]
    >([]);
    const [calendarDataReady, setCalendarDataReady] = useState<boolean>(false);
    const [selectedPolygon, setSelectedPolygon] = useState<string>("");
    const [selectedPolygonIndex, setSelectedPolygonIndex] = useState<number>(0);

    const polygonIdentifiers = props.benchmarkItems.map((item, index) => {
        const id: IIdentifier = {
            name: item.name,
            identifier: item.name.concat(index.toString()),
        };
        return id;
    });

    const createCalendarData = () => {
        const calendarData: Map<string, IChartVisitVisitorDatapoint[]> = new Map();
        calendarData.set(polygonIdentifiers[0].identifier, storeData.volumeByDay);

        setCalendarSelectionOneData(
            convertDayData(
                calendarData.get(polygonIdentifiers[0].identifier),
                SelectionTypesEnum.SELECTION_ONE_PRIMARY
            )
        );
        setCalendarSelectionTwoData(
            convertDayData(
                calendarData.get(polygonIdentifiers[0].identifier),
                SelectionTypesEnum.SELECTION_ONE_SECONDARY
            )
        );

        if (benchmarkData) {
            calendarData.set(
                polygonIdentifiers[1].identifier,
                benchmarkData?.volumeByDay
            );
        }

        return calendarData;
    };

    useEffect(() => {
        const data = mergeGPSData({
            selection1: storeData.volumeByWeek,
            selection2: benchmarkData?.volumeByWeek,
            national: null,
        });

        const calendarData = createCalendarData();

        setDayCalendarData(calendarData);
        setMergedCalendarData(data);
        setCalendarDataReady(true);
        setSelectedPolygon(location1Name);

        return () => setCalendarDataReady(false);
    }, []);

    for (const item of props.benchmarkItems) {
        useEffect(() => {
            const calendarData = createCalendarData();
            setDayCalendarData(calendarData);
        }, [item.name]);
    }

    const convertDayData = (
        data: IChartVisitVisitorDatapoint[],
        type: SelectionTypesEnum
    ): IChartCalendarDatapoint[] => {
        return data.map((item) => ({
            day: item.id.substring(0, item.id.length - 9),
            value:
                type === SelectionTypesEnum.SELECTION_ONE_SECONDARY
                    ? Number((item.visitWeight * 100).toFixed(3))
                    : Number((item.visitorWeight * 100).toFixed(3)),
        }));
    };

    const handleLocationDataChange = (value: string) => {
        const index = Number(value);
        setSelectedPolygonIndex(index);

        const polygon = polygonIdentifiers[index];
        const data = dayCalendarData.get(polygon.identifier);

        setCalendarSelectionOneData(
            convertDayData(data, SelectionTypesEnum.SELECTION_ONE_PRIMARY)
        );
        setCalendarSelectionTwoData(
            convertDayData(data, SelectionTypesEnum.SELECTION_ONE_SECONDARY)
        );
        setSelectedPolygon(polygonIdentifiers[index].name);
    };

    const getChartText = (visits: boolean): any => {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                }}
            >
                <span style={{ marginBottom: 5, fontWeight: 900 }}>
                    {visits ? "Visits" : "Visitors"} volume trends of{" "}
                    {polygonIdentifiers[0].name}{" "}
                    {polygonIdentifiers.length > 1
                        && "compared to " + polygonIdentifiers[1].name}
                    .
                </span>
                <span>{HH_ATTR_STATIC_TEXT}</span>
            </div>
        );
    };

    return (
        <ReportScrollableSection
            id="GPSVolume"
            style={{ justifyContent: "flex-start" }}
        >
            <ReportSectionHeader>
                <ReportSectionNumber>03</ReportSectionNumber>
                <ReportH1>Volume</ReportH1>
            </ReportSectionHeader>
            <AnimatedReportCard
                scrollableParentSelector="#GPSReportBody"
                animateIn="zoomIn"
                offset={100}
                animateOnce={true}
            >
                {featureFlags.DigitalShare && benchmarkData && location2Name && (
                    <GPSRelVolMet
                        location1Name={location1Name}
                        location2Name={location2Name}
                        buds={storeData.buds}
                        region={storeData.region}
                        storeEstVol={storeData.estimatedVolume}
                        visitsCount={storeData.visitsCount}
                        benchmarkEstVol={benchmarkData.estimatedVolume}
                        benchmarkVisitsCount={benchmarkData.visitsCount}
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.RelativeVolume}
                        renderType={props.renderType}
                        shareText={`Compares observed visit volume at ${location1Name} to ${location2Name}.`}
                    />
                )}
                {!benchmarkData && (
                    <GPSRelVolMet
                        location1Name={location1Name}
                        location2Name={null}
                        buds={storeData.buds}
                        region={storeData.region}
                        storeEstVol={storeData.estimatedVolume}
                        visitsCount={storeData.visitsCount}
                        benchmarkEstVol={null}
                        benchmarkVisitsCount={null}
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.RelativeVolume}
                        renderType={props.renderType}
                        shareText={`Compares observed visit volume at ${location1Name} to ${location2Name}.`}
                    />
                )}
                <TwoColumnGrid>
                    <ReportChartGroup
                        title="[visitVisitor] Volume Trend by Week"
                        data={mergeWeeklyData({
                            selection1: storeData.volumeByWeek,
                            selection2: benchmarkData?.volumeByWeek,
                            national: [],
                        })}
                        initialYValue="selection1PrimaryWeight"
                        primaryColor={colorTwoBright}
                        secondaryColor={colorTwoDark}
                        selection1Name={location1Name}
                        selection2Name={location2Name}
                        tooltipText="Provides how often the devices are visiting the geography of interest by week shown at the visits and visitors level. The first date in the range starts at the beginning of the week (Mon-Sun)."
                        xTickFormatter={formatWeek}
                        primaryWeightLabel="Visitors"
                        secondaryWeightLabel="Visits"
                        scrollableParentSelector="#GPSReportBody"
                        chartAnim="zoomIn"
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.VolumeByWeek}
                        renderType={props.renderType}
                        shareText={getChartText}
                        chartHeight={
                            props.renderType === RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                        }
                    />
                    <ReportChartGroup
                        title="[visitVisitor] Volume By Day of Week"
                        tooltipText="Weekday volume is the greatest on [greatestSel1Id] at [greatestSel1Value]"
                        data={mergeGPSData({
                            selection1: storeData.volumeByDOW,
                            selection2: benchmarkData?.volumeByDOW,
                            national: null,
                        })}
                        initialYValue="selection1PrimaryWeight"
                        primaryColor={colorTwoBright}
                        secondaryColor={colorTwoDark}
                        selection1Name={location1Name}
                        selection2Name={location2Name}
                        xTickFormatter={formatDOW}
                        primaryWeightLabel="Visitors"
                        secondaryWeightLabel="Visits"
                        scrollableParentSelector="#GPSReportBody"
                        chartAnim="zoomIn"
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.VolumeByDayOfWeek}
                        renderType={props.renderType}
                        shareText={getChartText}
                        chartHeight={
                            props.renderType === RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                        }
                    />
                    <ReportChartGroup
                        title="[visitVisitor] Volume By Hour of Day"
                        tooltipText="Daytime volume is the greatest at [greatestSel1Id] at [greatestSel1Value]"
                        data={mergeGPSData({
                            selection1: storeData.volumeByHour,
                            selection2: benchmarkData?.volumeByHour,
                            national: null,
                        })}
                        initialYValue="selection1PrimaryWeight"
                        primaryColor={colorTwoBright}
                        secondaryColor={colorTwoDark}
                        selection1Name={location1Name}
                        selection2Name={location2Name}
                        xTickFormatter={format24Hr}
                        primaryWeightLabel="Visitors"
                        secondaryWeightLabel="Visits"
                        scrollableParentSelector="#GPSReportBody"
                        chartAnim="zoomIn"
                        makeTheCardHeightMatchDemmit={true}
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.VolumeByHourOfDay}
                        renderType={props.renderType}
                        shareText={getChartText}
                        chartHeight={
                            props.renderType === RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                        }
                    />
                    {calendarDataReady && (
                        <ReportChartGroup
                            scrollableParentSelector="#GPSReportBody"
                            title="Day Calendar"
                            tooltipText="Visualizes seasonal trends, recurring patterns, and busy periods based on volume."
                            data={mergedCalendarData}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            selection1Name={location1Name}
                            selection2Name={location2Name}
                            chartAnim="zoomIn"
                            // TODO ERY: Need to update this day chart to have 2 dropdowns showing one or the other chart.
                            customChart={
                                <NivoCalendar
                                    storeData={storeData.volumeByDay}
                                    benchmarkData={benchmarkData?.volumeByDay}
                                    sel1Data={calendarSelectionOneData}
                                    sel2Data={calendarSelectionTwoData}
                                    sel1PrimaryName="Visitor"
                                    sel1SecondaryName="Visits"
                                    sel1Color={colorTwoBright}
                                    convertData={convertDayData}
                                    displayDropdown={true}
                                    customDropdownContent={
                                        // <PolygonNameWithCircle
                                        //   circleColor={location1Name === selectedPolygon ? "lightGreen" : "darkGreen"}
                                        //   circleWidth={12}
                                        //   circleHeight={12}
                                        //   children={
                                        //     <Select
                                        //       className='has-circle'
                                        //       defaultValue={location1Name}
                                        //       value={formatLabelWithEllipsis(selectedPolygon, 12)}
                                        //       onChange={(value: string) => handleLocationDataChange(value)}
                                        //     >
                                        //       {polygonIdentifiers.map((n, index) => {
                                        //         return (
                                        //           <Option value={n.identifier} key={n.name}>
                                        //             <PolygonNameWithCircle
                                        //               circleColor={index === 0 ? "lightGreen" : "darkGreen"}
                                        //               circleWidth={15}
                                        //               circleHeight={15}
                                        //             >
                                        //               {formatLabelWithEllipsis(n.name, 12)}
                                        //             </PolygonNameWithCircle>
                                        //           </Option>
                                        //         );
                                        //       })}
                                        //     </Select>
                                        //   }
                                        //   isSelect={true}
                                        // />
                                        <MobilyticsPolygonSelect
                                            benchmarkItems={props.benchmarkItems}
                                            selectedIndex={selectedPolygonIndex}
                                            onChange={handleLocationDataChange}
                                        />
                                    }
                                    location1Name={location1Name}
                                    location2Name={location2Name}
                                    app={DigitalShareAppType.Mobilytics}
                                    itemType={DigitalShareItemType.VolumeByDay}
                                    renderType={props.renderType}
                                />
                            }
                            unprocessedTextAboveChart={<div></div>}
                        />
                    )}
                </TwoColumnGrid>
                <GPSVolumeMetrics
                    scrollableParentSelector="#GPSReportBody"
                    storeData={storeData}
                    benchmarkData={benchmarkData}
                    chartAnim="zoomIn"
                    renderType={props.renderType}
                    itemType={DigitalShareItemType.VolumeByMetrics}
                    app={DigitalShareAppType.Mobilytics}
                />
            </AnimatedReportCard>
        </ReportScrollableSection>
    );
};

export default GPSInsightsVolume;
